/**
 * Master variants (1.2.0): Administration → Metadata model → Master variants (?view=variants), next to Asset classes.
 * The workspace's short list of editorial variants of a master (Programme, Textless, Clean feed, International (M&E),
 * Audio description mix, and any an administrator adds): a name, a code for file names and badges, "same picture
 * length required", and the order the pickers list them in. Against /api/v1/master-variants (step 0, frozen).
 */
import { useState } from 'react';
import { ArrowDown, ArrowUp, Plus, Save, Trash2 } from 'lucide-react';
import { MASTER_VARIANT_CODE, PROGRAMME_VARIANT_KEY, type MasterVariantDTO } from '@prism/contracts';
import { errorMessage } from '../../api/client';
import { useMasterVariantMutations, useMasterVariants, useMe } from '../../api/hooks';
import { toast } from '../../components/Toasts';
import { TableScroll } from '../../components/TableScroll';

interface Edit { name: string; code: string; sameLengthRequired: boolean }

const codeProblem = (code: string) => (code && !MASTER_VARIANT_CODE.test(code.trim().toUpperCase()) ? 'Use 1–12 letters, digits, - or _' : null);

export function MasterVariantsAdmin() {
  const q = useMasterVariants();
  const m = useMasterVariantMutations();
  const isAdmin = useMe().data?.permissions.includes('admin') ?? false;
  const [edits, setEdits] = useState<Record<string, Edit>>({});
  const [draft, setDraft] = useState<Edit>({ name: '', code: '', sameLengthRequired: true });
  const items = q.data?.items ?? [];
  const forget = (id: string) => setEdits((x) => { const n = { ...x }; delete n[id]; return n; });

  const save = async (v: MasterVariantDTO, e: Edit) => {
    try {
      await m.update.mutateAsync({ id: v.id, body: { name: e.name.trim(), code: e.code.trim(), sameLengthRequired: e.sameLengthRequired } });
      forget(v.id);
      toast(`Saved ${e.name.trim()}`);
    } catch (err) { toast(errorMessage(err), 'error'); }
  };
  const move = async (i: number, j: number) => {
    const ids = items.map((v) => v.id);
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    try { await m.reorder.mutateAsync(ids); } catch (err) { toast(errorMessage(err), 'error'); }
  };
  const remove = async (v: MasterVariantDTO) => {
    if (!window.confirm(`Delete the master variant “${v.name}”? Workflows that name it report it when they are saved or published next.`)) return;
    try { await m.remove.mutateAsync(v.id); toast(`Deleted ${v.name}`); } catch (err) { toast(errorMessage(err), 'error'); }
  };
  const add = async () => {
    try {
      const v = await m.create.mutateAsync({ name: draft.name.trim(), code: draft.code.trim(), sameLengthRequired: draft.sameLengthRequired });
      setDraft({ name: '', code: '', sameLengthRequired: true });
      toast(`Added ${v.name}`);
    } catch (err) { toast(errorMessage(err), 'error'); }
  };

  return (
    <div className="classes-embedded" data-testid="variants-admin">
      <p className="lede">
        An asset can hold several masters of the same content: other resolutions (a UHD and an HD master), and editorial variants such as a Textless or an
        International (M&amp;E) master. A master’s format (“UHD 2160p25 · ProRes 422 HQ”) comes from the file; its variant comes from this list. Masters are listed
        by variant, then format, in Export, Send to edit and QC, and the Library’s Has master filter uses them. The code is for file names and badges (“UHD · HD · TXL”).
        “Same length” means a master of the variant must be as long as the primary to within a frame; Textless is off by default, because textless elements are
        often appended at the end (the asset’s subtitles and markers then don’t apply to it).
      </p>
      <section className="panel-card" style={{ maxWidth: 820 }}>
        {q.isPending ? <div className="skeleton" style={{ height: 180 }} /> : (
          <TableScroll>
          <table className="table compact">
            <thead><tr><th>Name</th><th>Code</th><th title="A master of this variant must match the primary's duration to within a frame">Same length</th><th>Masters</th><th /></tr></thead>
            <tbody>
              {items.map((v, i) => {
                const e = edits[v.id] ?? { name: v.name, code: v.code, sameLengthRequired: v.sameLengthRequired };
                const dirty = e.name !== v.name || e.code !== v.code || e.sameLengthRequired !== v.sameLengthRequired;
                const problem = codeProblem(e.code);
                const programme = v.key === PROGRAMME_VARIANT_KEY;
                return (
                  <tr key={v.id} data-testid="variant-row" data-variant-key={v.key ?? undefined}>
                    <td><input className="input" value={e.name} disabled={!isAdmin} aria-label={`Name of ${v.name}`} maxLength={60} onChange={(ev) => setEdits({ ...edits, [v.id]: { ...e, name: ev.target.value } })} /></td>
                    <td style={{ width: 120 }}>
                      <input className="input mono" value={e.code} disabled={!isAdmin} aria-label={`Code of ${v.name}`} maxLength={12} aria-invalid={Boolean(problem) || undefined} title={problem ?? undefined}
                        onChange={(ev) => setEdits({ ...edits, [v.id]: { ...e, code: ev.target.value.toUpperCase() } })} />
                    </td>
                    <td><input type="checkbox" checked={e.sameLengthRequired} disabled={!isAdmin} aria-label={`${v.name}: same length required`} onChange={(ev) => setEdits({ ...edits, [v.id]: { ...e, sameLengthRequired: ev.target.checked } })} /></td>
                    <td className="muted" data-testid="variant-in-use">{v.inUse}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {dirty && isAdmin && <button className="btn primary sm" disabled={!e.name.trim() || !e.code.trim() || Boolean(problem)} onClick={() => void save(v, e)} data-testid="variant-save"><Save size={12} /> Save</button>}
                      <button className="btn ghost icon sm" disabled={!isAdmin || i === 0 || m.reorder.isPending} aria-label={`Move ${v.name} up`} onClick={() => void move(i, i - 1)}><ArrowUp size={12} /></button>
                      <button className="btn ghost icon sm" disabled={!isAdmin || i === items.length - 1 || m.reorder.isPending} aria-label={`Move ${v.name} down`} onClick={() => void move(i, i + 1)}><ArrowDown size={12} /></button>
                      <button className="btn ghost icon sm danger" aria-label={`Delete ${v.name}`} disabled={!isAdmin || programme || v.inUse > 0}
                        title={programme ? 'Programme is the variant of every master without one; it cannot be deleted' : v.inUse ? `${v.inUse} master${v.inUse === 1 ? ' has' : 's have'} this variant; change their variant first` : undefined}
                        onClick={() => void remove(v)}><Trash2 size={12} /></button>
                    </td>
                  </tr>
                );
              })}
              {isAdmin && (
                <tr>
                  <td><input className="input" placeholder="New variant, e.g. Trailer cut" value={draft.name} maxLength={60} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="new-variant-name" aria-label="Name of the new variant" /></td>
                  <td><input className="input mono" placeholder="Code" value={draft.code} maxLength={12} onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })} data-testid="new-variant-code" aria-label="Code of the new variant" aria-invalid={Boolean(codeProblem(draft.code)) || undefined} /></td>
                  <td><input type="checkbox" checked={draft.sameLengthRequired} onChange={(e) => setDraft({ ...draft, sameLengthRequired: e.target.checked })} aria-label="New variant: same length required" /></td>
                  <td />
                  <td><button className="btn sm" disabled={!draft.name.trim() || !draft.code.trim() || Boolean(codeProblem(draft.code)) || m.create.isPending} onClick={() => void add()} data-testid="add-variant"><Plus size={12} /> Add</button></td>
                </tr>
              )}
            </tbody>
          </table>
          </TableScroll>
        )}
        {codeProblem(draft.code) && <p className="faint" style={{ fontSize: 12 }}>{codeProblem(draft.code)}</p>}
      </section>
    </div>
  );
}
